/**
 * Validador centralizado para dados do sistema PsicolSys
 * Garante integridade e consistência dos dados
 */

// Tipos importados conforme necessário nas funções de validação
import { isValidISOTimestamp } from './timestampNormalizer';
import { ensureLocalDate } from './dateUtils';

// Tipos para resultados de validação
export interface ValidationResult {
  isValid: boolean;
  errors: string[];
  warnings: string[];
}

export interface ValidationOptions {
  strict?: boolean; // Se true, warnings são tratados como errors
  skipOptionalFields?: boolean;
}

/**
 * Valida dados de paciente
 */
export function validatePatient(patient: any, options: ValidationOptions = {}): ValidationResult {
  const errors: string[] = [];
  const warnings: string[] = [];

  // Validações obrigatórias
  if (!patient) {
    errors.push('Dados do paciente são obrigatórios');
    return { isValid: false, errors, warnings };
  }

  // ID é opcional durante criação, obrigatório apenas para edição
  if (patient.id !== undefined && (typeof patient.id !== 'string' || patient.id.trim().length === 0)) {
    errors.push('ID do paciente deve ser uma string válida quando fornecido');
  }

  if (!patient.name || typeof patient.name !== 'string' || patient.name.trim().length === 0) {
    errors.push('Nome do paciente é obrigatório');
  }

  if (patient.name && patient.name.length > 200) {
    errors.push('Nome do paciente não pode exceder 200 caracteres');
  }

  // Idade pode ser 0 ou undefined (será calculada automaticamente)
  if (patient.age !== undefined && (typeof patient.age !== 'number' || patient.age < 0 || patient.age > 150)) {
    errors.push('Idade deve ser um número válido entre 0 e 150');
  }

  // Gênero mais flexível - aceitar qualquer string não vazia
  if (!patient.gender || typeof patient.gender !== 'string' || patient.gender.trim().length === 0) {
    errors.push('Gênero é obrigatório');
  }

  // Validações de campos obrigatórios específicos do formulário
  if (!patient.birthDate || typeof patient.birthDate !== 'string' || patient.birthDate.trim().length === 0) {
    errors.push('Data de nascimento é obrigatória');
  }

  if (!patient.firstConsultDate || typeof patient.firstConsultDate !== 'string' || patient.firstConsultDate.trim().length === 0) {
    errors.push('Data da primeira consulta é obrigatória');
  }

  if (!patient.phone || typeof patient.phone !== 'string' || patient.phone.trim().length === 0) {
    errors.push('Telefone é obrigatório');
  }

  // Validações de timestamps (apenas avisos, pois são gerados automaticamente)
  if (patient.createdAt && !isValidISOTimestamp(patient.createdAt)) {
    warnings.push('Data de criação deve estar no formato ISO válido');
  }

  if (patient.updated_at && !isValidISOTimestamp(patient.updated_at)) {
    warnings.push('Data de atualização deve estar no formato ISO válido');
  }

  // Validações opcionais mais flexíveis
  if (!options.skipOptionalFields) {
    if (patient.phone && patient.phone.length > 50) {
      warnings.push('Telefone não deve exceder 50 caracteres');
    }

    if (patient.email && patient.email && !isValidEmail(patient.email)) {
      warnings.push('Email deve ter formato válido');
    }

    if (patient.address && typeof patient.address !== 'string') {
      warnings.push('Endereço deve ser uma string');
    }

    if (patient.birthDate && !isValidDate(patient.birthDate)) {
      warnings.push('Data de nascimento deve estar em formato válido');
    }

    if (patient.firstConsultDate && !isValidDate(patient.firstConsultDate)) {
      warnings.push('Data da primeira consulta deve estar em formato válido');
    }
  }

  // Verificar se há campos extras não esperados
  const allowedFields = [
    'id', 'name', 'age', 'gender', 'phone', 'email', 'address', 
    'firstConsultDate', 'emergencyContact', 'emergencyPhone', 
    'referenceContact', 'referencePhone', 'previousTreatmentHistory',
    'createdAt', 'updatedAt', 'deleted', 'birthDate', 'maritalStatus',
    'zipCode', 'addressNumber', 'neighborhood', 'city', 'state',
    'sessionPrice', 'created_at', 'updated_at', 'deleted_at', 'deletedAt',
    'timestamp', 'user_id'
  ];

  Object.keys(patient).forEach(key => {
    if (!allowedFields.includes(key)) {
      warnings.push(`Campo não reconhecido: ${key}`);
    }
  });

  const finalErrors = options.strict ? [...errors, ...warnings] : errors;
  return {
    isValid: finalErrors.length === 0,
    errors: finalErrors,
    warnings: options.strict ? [] : warnings
  };
}

/**
 * Valida dados de sessão
 */
export function validateSession(session: any, options: ValidationOptions = {}): ValidationResult {
  const errors: string[] = [];
  const warnings: string[] = [];

  if (!session) {
    errors.push('Dados da sessão são obrigatórios');
    return { isValid: false, errors, warnings };
  }

  if (!session.id || typeof session.id !== 'string') {
    errors.push('ID da sessão é obrigatório e deve ser uma string');
  }

  if (!session.patientId || typeof session.patientId !== 'string') {
    errors.push('ID do paciente é obrigatório na sessão');
  }

  if (!session.date || !isValidDate(session.date)) {
    errors.push('Data da sessão é obrigatória e deve estar em formato válido');
  }

  // Validações de timestamps
  if (session.createdAt && !isValidISOTimestamp(session.createdAt)) {
    errors.push('Data de criação deve estar no formato ISO válido');
  }

  if (session.updated_at && !isValidISOTimestamp(session.updated_at)) {
    errors.push('Data de atualização deve estar no formato ISO válido');
  }

  // Validar conteúdo/notas
  if (session.content && typeof session.content !== 'string') {
    errors.push('Conteúdo da sessão deve ser uma string');
  }

  if (session.notes && typeof session.notes !== 'string') {
    errors.push('Notas da sessão devem ser uma string');
  }

  // Validar anexos
  if (session.attachments) {
    if (!Array.isArray(session.attachments)) {
      errors.push('Anexos devem ser um array');
    } else {
      session.attachments.forEach((attachment: any, index: number) => {
        if (!attachment.id || typeof attachment.id !== 'string') {
          errors.push(`Anexo ${index}: ID é obrigatório`);
        }
        if (!attachment.name || typeof attachment.name !== 'string') {
          errors.push(`Anexo ${index}: Nome é obrigatório`);
        }
        if (!attachment.type || typeof attachment.type !== 'string') {
          errors.push(`Anexo ${index}: Tipo é obrigatório`);
        }
      });
    }
  }

  const allowedFields = [
    'id', 'patientId', 'date', 'content', 'notes', 'attachments',
    'createdAt', 'updatedAt', 'deleted', 'time', 'created_at', 'updated_at',
    'deleted_at', 'deletedAt', 'timestamp', 'user_id', 'patientName'
  ];

  Object.keys(session).forEach(key => {
    if (!allowedFields.includes(key)) {
      warnings.push(`Campo não reconhecido: ${key}`);
    }
  });

  const finalErrors = options.strict ? [...errors, ...warnings] : errors;
  return {
    isValid: finalErrors.length === 0,
    errors: finalErrors,
    warnings: options.strict ? [] : warnings
  };
}

/**
 * Valida dados de agendamento
 */
export function validateAppointment(appointment: any, options: ValidationOptions = {}): ValidationResult {
  const errors: string[] = [];
  const warnings: string[] = [];

  if (!appointment) {
    errors.push('Dados do agendamento são obrigatórios');
    return { isValid: false, errors, warnings };
  }

  if (!appointment.id || typeof appointment.id !== 'string') {
    errors.push('ID do agendamento é obrigatório e deve ser uma string');
  }

  if (!appointment.patientId || typeof appointment.patientId !== 'string') {
    errors.push('ID do paciente é obrigatório no agendamento');
  }

  if (!appointment.date || !isValidDate(appointment.date)) {
    errors.push('Data do agendamento é obrigatória e deve estar em formato válido');
  }

  if (!appointment.time || typeof appointment.time !== 'string') {
    errors.push('Horário do agendamento é obrigatório');
  }

  if (appointment.time && !isValidTime(appointment.time)) {
    errors.push('Horário deve estar no formato HH:MM');
  }

  // Validações de timestamps
  if (appointment.createdAt && !isValidISOTimestamp(appointment.createdAt)) {
    errors.push('Data de criação deve estar no formato ISO válido');
  }

  if (appointment.updated_at && !isValidISOTimestamp(appointment.updated_at)) {
    errors.push('Data de atualização deve estar no formato ISO válido');
  }

  // Validar status de comparecimento
  if (appointment.attendanceStatus !== null && appointment.attendanceStatus !== undefined && 
      !['present', 'absent'].includes(appointment.attendanceStatus)) {
    errors.push('Status de comparecimento deve ser: present, absent ou null');
  }

  const allowedFields = [
    'id', 'patientId', 'patientName', 'date', 'time', 'notes', 
    'attendanceStatus', 'createdAt', 'updatedAt', 'deleted',
    'sessionPrice', 'created_at', 'updated_at', 'status',
    'deleted_at', 'deletedAt', 'timestamp', 'user_id'
  ];

  Object.keys(appointment).forEach(key => {
    if (!allowedFields.includes(key)) {
      warnings.push(`Campo não reconhecido: ${key}`);
    }
  });

  const finalErrors = options.strict ? [...errors, ...warnings] : errors;
  return {
    isValid: finalErrors.length === 0,
    errors: finalErrors,
    warnings: options.strict ? [] : warnings
  };
}

/**
 * Valida array de dados
 */
export function validateDataArray(
  data: any[], 
  validator: (item: any, options?: ValidationOptions) => ValidationResult,
  options: ValidationOptions = {}
): ValidationResult {
  const errors: string[] = [];
  const warnings: string[] = [];

  if (!Array.isArray(data)) {
    errors.push('Dados devem ser um array');
    return { isValid: false, errors, warnings };
  }

  data.forEach((item, index) => {
    const result = validator(item, options);
    if (!result.isValid) {
      errors.push(`Item ${index}: ${result.errors.join(', ')}`);
    }
    if (result.warnings.length > 0) {
      warnings.push(`Item ${index}: ${result.warnings.join(', ')}`);
    }
  });

  const finalErrors = options.strict ? [...errors, ...warnings] : errors;
  return {
    isValid: finalErrors.length === 0,
    errors: finalErrors,
    warnings: options.strict ? [] : warnings
  };
}

/**
 * Valida estrutura completa de dados do usuário
 */
export function validateUserData(userData: any, options: ValidationOptions = {}): ValidationResult {
  const errors: string[] = [];
  const warnings: string[] = [];

  if (!userData) {
    errors.push('Dados do usuário são obrigatórios');
    return { isValid: false, errors, warnings };
  }

  // Validar estrutura principal
  if (!userData.data) {
    errors.push('Propriedade "data" é obrigatória');
    return { isValid: false, errors, warnings };
  }

  const { data } = userData;

  // Validar pacientes
  if (data.patients) {
    const patientsResult = validateDataArray(data.patients, validatePatient, options);
    if (!patientsResult.isValid) {
      errors.push(`Pacientes: ${patientsResult.errors.join(', ')}`);
    }
    warnings.push(...patientsResult.warnings.map(w => `Pacientes: ${w}`));
  }

  // Validar sessões
  if (data.sessions) {
    const sessionsResult = validateDataArray(data.sessions, validateSession, options);
    if (!sessionsResult.isValid) {
      errors.push(`Sessões: ${sessionsResult.errors.join(', ')}`);
    }
    warnings.push(...sessionsResult.warnings.map(w => `Sessões: ${w}`));
  }

  // Validar agendamentos
  if (data.appointments) {
    const appointmentsResult = validateDataArray(data.appointments, validateAppointment, options);
    if (!appointmentsResult.isValid) {
      errors.push(`Agendamentos: ${appointmentsResult.errors.join(', ')}`);
    }
    warnings.push(...appointmentsResult.warnings.map(w => `Agendamentos: ${w}`));
  }

  const finalErrors = options.strict ? [...errors, ...warnings] : errors;
  return {
    isValid: finalErrors.length === 0,
    errors: finalErrors,
    warnings: options.strict ? [] : warnings
  };
}

// Funções auxiliares de validação
function isValidEmail(email: string): boolean {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
}

function isValidDate(date: string): boolean {
  if (typeof date !== 'string') return false;
  const parsedDate = ensureLocalDate(date);
  return !isNaN(parsedDate.getTime());
}

function isValidTime(time: string): boolean {
  if (typeof time !== 'string') {
    console.warn('Validação de horário falhou: tipo inválido', { time, type: typeof time });
    return false;
  }
  
  // Regex mais flexível para aceitar diferentes formatos de horário
  const timeRegex = /^([01]?[0-9]|2[0-3]):([0-5][0-9])$/;
  const isValid = timeRegex.test(time.trim());
  
  // Debug: log valores que estão falhando na validação
  if (!isValid) {
    console.warn('Validação de horário falhou:', {
      time,
      trimmed: time.trim(),
      type: typeof time,
      length: time?.length,
      regex: timeRegex.toString()
    });
  }
  
  return isValid;
}

/**
 * Sanitiza dados removendo campos inválidos
 */
export function sanitizeData<T>(data: T, validator: (item: any) => ValidationResult): T {
  if (!data) return data;
  
  const result = validator(data);
  if (result.isValid) return data;
  
  // Para dados inválidos, retornar uma versão limpa
  console.warn('Dados inválidos detectados, aplicando sanitização:', result.errors);
  
  return data; // Por enquanto, retorna os dados originais
}

/**
 * Validação simplificada para formulários (mais tolerante)
 */
export function validatePatientForm(patient: any): ValidationResult {
  const errors: string[] = [];
  const warnings: string[] = [];

  if (!patient) {
    errors.push('Dados do paciente são obrigatórios');
    return { isValid: false, errors, warnings };
  }

  // Apenas campos realmente obrigatórios no formulário
  if (!patient.name || typeof patient.name !== 'string' || patient.name.trim().length === 0) {
    errors.push('Nome do paciente é obrigatório');
  }

  if (!patient.birthDate || typeof patient.birthDate !== 'string' || patient.birthDate.trim().length === 0) {
    errors.push('Data de nascimento é obrigatória');
  }

  if (!patient.gender || typeof patient.gender !== 'string' || patient.gender.trim().length === 0) {
    errors.push('Gênero é obrigatório');
  }

  if (!patient.phone || typeof patient.phone !== 'string' || patient.phone.trim().length === 0) {
    errors.push('Telefone é obrigatório');
  }

  return {
    isValid: errors.length === 0,
    errors,
    warnings
  };
}

/**
 * Valida e limpa dados antes de salvar
 */
export function validateAndCleanBeforeSave(data: any, type: 'patient' | 'session' | 'appointment'): any {
  let validator: (item: any) => ValidationResult;
  
  switch (type) {
    case 'patient':
      // Para pacientes, usar validação mais tolerante do formulário
      validator = validatePatientForm;
      break;
    case 'session':
      validator = validateSession;
      break;
    case 'appointment':
      validator = validateAppointment;
      break;
    default:
      throw new Error(`Tipo de validação não suportado: ${type}`);
  }
  
  const result = validator(data);
  
  if (!result.isValid) {
    console.error(`Dados inválidos para ${type}:`, {
      data,
      errors: result.errors
    });
    throw new Error(`Dados inválidos: ${result.errors.join(', ')}`);
  }
  
  if (result.warnings.length > 0) {
    console.warn(`Avisos para ${type}:`, result.warnings);
  }
  
  return data;
}